import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useProfile } from '../../contexts/ProfileContext'
import { isPublicPath } from '../../lib/routes/publicRoutes'

/**
 * Debug component that logs routing decisions
 * Only active in development builds
 */
export function RouteDebug() {
  const location = useLocation()
  const { user, loading } = useAuth()
  const { profile } = useProfile()

  useEffect(() => {
    if (!import.meta.env.DEV) return

    console.group('[RouteDebug]')
    console.log('Path:', location.pathname)
    console.log('Search:', location.search)
    console.log('Hash:', location.hash)
    console.log('State:', location.state)
    console.log('User:', user ? `${user.email} (${user.id})` : 'none')
    console.log('Profile:', profile ? `${profile.role} (${profile.id})` : loading ? 'loading' : 'none')
    console.log('Auth Loading:', loading)

    // Determine expected guard behavior
    const isPublicRoute = isPublicPath(location.pathname)

    const isAdminRoute = location.pathname.startsWith('/admin')
    const isPortalRoute = location.pathname.startsWith('/portal')

    console.log('Route Type:', {
      isPublic: isPublicRoute,
      isAdmin: isAdminRoute,
      isPortal: isPortalRoute,
    })

    // Guard decisions
    if (isPublicRoute) {
      console.log('✓ Public route - no auth required')
    } else if (!loading) {
      if (!user && !isPublicRoute) {
        console.warn('⚠️ Unauthenticated user on protected route')
      }

      if (user && profile) {
        const isAdmin = profile.role === 'admin' || profile.role === 'owner'
        const isEmployee = profile.role === 'employee' || profile.role === 'manager'

        if (isAdmin && isPortalRoute && !isPublicRoute) {
          console.warn('⚠️ Admin on portal route (should redirect to /admin)')
        }

        if (isEmployee && isAdminRoute && !isPublicRoute) {
          console.warn('⚠️ Employee on admin route (should redirect to /portal)')
        }

        console.log('✓ Guard OK for', profile.role)
      }
    }

    console.groupEnd()
  }, [location, user, profile, loading])

  // Show debug badge in dev
  if (!import.meta.env.DEV) return null

  return (
    // Marked so accessibility scans can exclude it. It is a development
    // affordance, not product, and 138 of its nodes were drowning the real
    // contrast failures in the report.
    <div data-dev-overlay
         className="fixed bottom-4 left-4 z-50 bg-brand text-white px-3 py-2 rounded-lg shadow-lg text-xs font-mono">
      <div className="flex items-center gap-2">
        <span className="font-bold">DEV</span>
        <span className="opacity-75">|</span>
        <span>{location.pathname}</span>
        {profile && (
          <>
            <span className="opacity-75">|</span>
            <span className="text-[#475569]">{profile.role}</span>
          </>
        )}
      </div>
    </div>
  )
}
